import axios from "axios"
import Vue from 'vue'
import { Message } from 'element-ui'

const BASE_URL = process.env.VUE_APP_BASE_URL || "http://localhost:4200"
const AUTH_BASE_URL = process.env.VUE_APP_AUTH_BASE_URL || "http://localhost:4201/auth"

// 防止重复刷新token的标识
let isRefreshing = false
let failedQueue = []

const processQueue = (error, token = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error)
    } else {
      resolve(token)
    }
  })

  failedQueue = []
}

// 请求拦截器：自动添加 JWT token
axios.interceptors.request.use(
  config => {
    const token = localStorage.getItem('access_token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  error => {
    return Promise.reject(error)
  }
)

// 响应拦截器：处理 token 过期
axios.interceptors.response.use(
  response => response,
  async error => {
    const originalRequest = error.config

    // 处理 401 Unauthorized (token 过期)
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !originalRequest.skipAuthRefresh
    ) {
      originalRequest._retry = true

      // 如果正在刷新token，将请求加入队列
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject })
        }).then(token => {
          originalRequest.headers.Authorization = `Bearer ${token}`
          return axios(originalRequest)
        }).catch(err => {
          return Promise.reject(err)
        })
      }

      try {
        const refreshToken = localStorage.getItem('refresh_token')
        if (!refreshToken) {
          throw new Error('No refresh token')
        }

        isRefreshing = true

        const response = await axios.post(`${AUTH_BASE_URL}/refresh`, {
          refresh_token: refreshToken
        })

        // 更新 token
        const newAccessToken = response.data.access_token
        localStorage.setItem('access_token', newAccessToken)
        localStorage.setItem('refresh_token', response.data.refresh_token)

        // 处理队列中的请求
        processQueue(null, newAccessToken)

        // 重试原请求
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`
        return axios(originalRequest)
      } catch (refreshError) {
        console.error('Token refresh failed:', refreshError)

        // 处理队列中的请求，全部失败
        processQueue(refreshError, null)

        // 刷新失败，清除 token 并跳转登录
        localStorage.removeItem('access_token')
        localStorage.removeItem('refresh_token')

        if (window.location.hash !== '#/login') {
          Message.warning('登录已过期，请重新登录')
          window.location.href = '/#/login'
        }

        return Promise.reject(refreshError)
      } finally {
        isRefreshing = false
      }
    }

    // 处理 403 Forbidden (访问权限)
    if (error.response?.status === 403) {
      Message.error('您没有权限访问此数据')
      return Promise.reject(error)
    }

    return Promise.reject(error)
  }
)

// 上传 DICOM ZIP 文件，支持前端进度回调
// params: { patient_name, study_date, file, onProgress?: (percent:number, loaded:number, total:number, rawEvent:ProgressEvent) => void }
export async function uploadDicomZip({ patient_name, study_date, file, onProgress }) {
  return axios.put(`${BASE_URL}/upload_dicom_raw`, file, {
    params: {
      patient_name,
      study_date,
      filename: file && file.name ? file.name : 'dicom.zip',
      file_size: file && file.size != null ? file.size : undefined
    },
    headers: { "Content-Type": (file && file.type) || "application/zip" },
    onUploadProgress: (evt) => {
      if (!onProgress) return
      const total = evt.total || (file && file.size) || 0
      const loaded = evt.loaded
      let percent = 0
      if (total > 0) {
        percent = Math.min(100, Math.round((loaded / total) * 100))
      }
      onProgress(percent, loaded, total, evt)
    }
  })
}

// 处理数据
export async function processCase(patient_name, study_date) {
  return axios.post(`${BASE_URL}/process/${encodeURIComponent(patient_name)}/${study_date}`)
}

// 获取所有已处理病人-日期列表
export async function listPatients(ownerId = null) {
  return axios.get(`${BASE_URL}/list_patients${ownerQuery(ownerId)}`)
}

export async function deletePatient(patient_name, study_date) {
  return axios.delete(`${BASE_URL}/delete_patient/${encodeURIComponent(patient_name)}/${study_date}`)
}

function ownerQuery(ownerId) {
  return ownerId ? `?owner_id=${encodeURIComponent(ownerId)}` : ''
}

function addOwnerParam(params, ownerId) {
  if (ownerId) params.append('owner_id', ownerId)
}

export async function getCaseAssets(patient_name, study_date, ownerId = null) {
  return axios.get(`${BASE_URL}/get_case_assets/${encodeURIComponent(patient_name)}/${study_date}${ownerQuery(ownerId)}`)
}

export async function downloadOutputFolder(patient_name, study_date, folder, ownerId = null) {
  return axios.get(`${BASE_URL}/download_output_folder/${encodeURIComponent(patient_name)}/${study_date}/${encodeURIComponent(folder)}${ownerQuery(ownerId)}`, {
    responseType: 'blob'
  })
}

export async function getStatisticsSummary(userId = null, filters = {}) {
  const params = new URLSearchParams()
  if (userId) params.append('user_id', userId)
  if (filters.patient_name) params.append('patient_name', filters.patient_name)
  if (filters.study_date) params.append('study_date', filters.study_date)
  const q = params.toString() ? `?${params.toString()}` : ''
  return axios.get(`${BASE_URL}/statistics/summary${q}`)
}

export async function getMiddleOnlyStatisticsSummary(userId = null, patients = []) {
  const params = new URLSearchParams()
  if (userId) params.append('user_id', userId)
  const q = params.toString() ? `?${params.toString()}` : ''
  return axios.post(`${BASE_URL}/statistics/middle-only-summary${q}`, { patients })
}

export async function exportStatisticsCsv(userId = null, patients = [], ownerName = null) {
  const params = new URLSearchParams()
  if (userId) params.append('user_id', userId)
  if (ownerName) params.append('owner_name', ownerName)
  const q = params.toString() ? `?${params.toString()}` : ''
  return axios.post(`${BASE_URL}/statistics/export${q}`, { patients }, { responseType: 'blob' })
}

// 获取关键结果（csv内容和图片名）
export async function getKeyResults(patient_name, study_date, ownerId = null) {
  return axios.get(`${BASE_URL}/get_key_results/${encodeURIComponent(patient_name)}/${study_date}${ownerQuery(ownerId)}`)
}

export async function getAnalysisCalculation(patient_name, study_date, ownerId = null) {
  return axios.get(`${BASE_URL}/analysis_calculation/${encodeURIComponent(patient_name)}/${study_date}${ownerQuery(ownerId)}`)
}

export async function saveAnalysisCalculation(patient_name, study_date, payload, ownerId = null) {
  return axios.post(`${BASE_URL}/analysis_calculation/${encodeURIComponent(patient_name)}/${study_date}${ownerQuery(ownerId)}`, payload)
}

export async function selectMiddleSlice(patient_name, study_date, filename, ownerId = null) {
  return axios.post(`${BASE_URL}/select_middle_slice/${encodeURIComponent(patient_name)}/${study_date}${ownerQuery(ownerId)}`, {
    filename
  })
}

//添加时间戳和 token（用于认证）
export function getImageUrl(patient_name, study_date, filename, ownerId = null) {
  const token = localStorage.getItem('access_token');
  const params = new URLSearchParams();
  params.append('t', Date.now());
  addOwnerParam(params, ownerId);
  if (token) {
    params.append('token', token);
  }
  return `${BASE_URL}/get_image/${encodeURIComponent(patient_name)}/${study_date}/${filename}?${params.toString()}`;
}

// L3 检测
export async function l3Detect(patient_name, study_date) {
  return axios.post(`${BASE_URL}/l3_detect/${encodeURIComponent(patient_name)}/${study_date}`);
}

// 手动上传 L3 mask
export async function uploadL3Mask(patient, date, file) {
  const formData = new FormData();
  formData.append("file", file);
  return axios.post(`${BASE_URL}/upload_l3_mask/${encodeURIComponent(patient)}/${date}`, formData, {
    headers: { "Content-Type": "multipart/form-data" }
  });
}

// L3 之后的流程
export async function continueAfterL3(patient_name, study_date) {
  return axios.post(`${BASE_URL}/continue_after_l3/${encodeURIComponent(patient_name)}/${study_date}`);
}

// 获取 L3 相关图片（修复：正确处理URL参数，添加时间戳和token）
// bustCache: 是否添加时间戳破坏缓存，默认false（允许浏览器缓存）
export function getL3ImageUrl(patient_name, study_date, folder, filename, bustCache = false, ownerId = null) {
  const token = localStorage.getItem('access_token');
  const params = new URLSearchParams();
  if (bustCache) {
    params.append('t', Date.now());  // 只在需要时添加时间戳防止缓存
  }
  addOwnerParam(params, ownerId);
  if (token) {
    params.append('token', token);
  }
  return `${BASE_URL}/get_output_image/${encodeURIComponent(patient_name)}/${study_date}/${folder}/${filename}?${params.toString()}`;
}

// 生成侧视图（sagittal）
export async function generateSagittal(patient_name, study_date, force = 0) {
  return axios.post(`${BASE_URL}/generate_sagittal/${encodeURIComponent(patient_name)}/${study_date}?force=${force}`);
}

export function getAxisalImageUrl(patient_name, study_date, filename, ownerId = null) {
  return getL3ImageUrl(patient_name, study_date, 'Axisal', filename, false, ownerId)
}

// 手动上传 Middle 原图的 psoas/combo mask
export async function uploadMiddleManualMask(patient, date, psoasFile, comboFile, ownerId = null) {
  const fd = new FormData()
  if (psoasFile) fd.append('psoas_mask', psoasFile)
  if (comboFile) fd.append('combo_mask', comboFile)
  try {
    const res = await axios.post(
      `${BASE_URL}/upload_middle_manual_mask/${encodeURIComponent(patient)}/${encodeURIComponent(date)}${ownerQuery(ownerId)}`,
      fd,
      { headers: { 'Accept': 'application/json' } }
    )
    return res
  } catch (e) {
    // 让调用方能看到服务器的错误信息
    if (e.response) {
      console.error('[uploadMiddleManualMask] server error:', e.response.status, e.response.data)
      throw new Error((e.response.data && e.response.data.error) || 'HTTP ' + e.response.status);
    } else {
      console.error('[uploadMiddleManualMask] network error:', e)
      throw e
    }
  }
}

// 查询任务状态
export async function getTaskStatus(taskId) {
  return axios.get(`${BASE_URL}/task_status/${taskId}`);
}

export async function getL3QualityWarnings(patient_name, study_date) {
  return axios.get(`${BASE_URL}/l3_quality_warnings/${encodeURIComponent(patient_name)}/${study_date}`);
}

// 列出所有任务
export async function listTasks() {
  return axios.get(`${BASE_URL}/list_tasks`);
}

// 查询上传进度
export async function getUploadStatus(upload_id) {
  return axios.get(`${BASE_URL}/upload_status/${upload_id}`)
}

// ==================== SAM2 分割 API ====================

/**
 * SAM2 分割 (支持交互式点击)
 * @param {Object} params - 参数对象
 * @param {Blob|File} params.imageFile - 图像文件对象
 * @param {string} params.imageType - 图像类型 ("L3", "middle", "auto")
 * @param {string} params.patientId - 患者ID (可选)
 * @param {string} params.sliceIndex - 切片索引 (可选)
 * @param {Array} params.clickPoints - 点击坐标数组 (可选)
 *                例: [{x: 100, y: 200, label: 1}]
 *                label: 1=前景点, 0=背景点
 * @returns {Promise<Object>} - 返回分割结果
 * @returns {string} .mask_data - Base64 编码的 mask PNG 图像
 * @returns {number} .confidence_score - 置信度 (0.0-1.0)
 * @returns {number} .processing_time_ms - 处理时间 (毫秒)
 * @returns {boolean} .cached - 是否来自缓存
 * @returns {Object} .bounding_box - 边界框 {x, y, width, height}
 */
export async function sam2Segment({ imageFile, imageType = 'auto', patientId = null, sliceIndex = null, clickPoints = null }) {
  try {
    const formData = new FormData()
    formData.append('file', imageFile)
    formData.append('image_type', imageType)
    formData.append('return_format', 'base64') // 确保返回 base64 格式

    // 添加点击坐标
    if (clickPoints && clickPoints.length > 0) {
      formData.append('click_points', JSON.stringify(clickPoints))
    }

    // SAM2 API 直接通过 Nginx 代理，不使用 BASE_URL
    const response = await axios.post(
      '/api/segmentation/segment', // 使用绝对路径，直接通过 Nginx 代理
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        timeout: 125000 // 125秒超时 (服务端120秒 + 5秒缓冲)
      }
    )

    return response.data
  } catch (error) {
    // 增强错误处理
    if (error.response) {
      const status = error.response.status
      const detail = error.response.data?.detail || error.response.data?.message || 'Unknown error'

      if (status === 503) {
        throw new Error('SAM2 服务暂时不可用，请稍后重试')
      } else if (status === 400) {
        throw new Error(`无效的图像格式: ${detail}`)
      } else if (status === 500) {
        throw new Error(`分割失败: ${detail}`)
      } else {
        throw new Error(`请求失败 (${status}): ${detail}`)
      }
    } else if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
      throw new Error('SAM2 分割超时，请重试')
    } else {
      throw new Error(`网络错误: ${error.message}`)
    }
  }
}

/**
 * 检查 SAM2 服务健康状态
 * @returns {Promise<Object>} - 返回健康状态
 * @returns {boolean} .enabled - SAM2 是否启用
 * @returns {boolean} .available - SAM2 是否可用
 * @returns {Object} .cache_stats - 缓存统计信息
 */
export async function checkSam2Health() {
  try {
    // SAM2 健康检查直接通过 Nginx 代理，不使用 BASE_URL
    const response = await axios.get('/api/segmentation/health', {
      timeout: 5000
    })
    return {
      enabled: true,
      available: response.data.status === 'healthy',
      model_loaded: response.data.model_loaded
    }
  } catch (error) {
    console.error('SAM2 health check failed:', error)
    return {
      enabled: false,
      available: false,
      cache_stats: {}
    }
  }
}

// ==================== SAM2 分割 API 结束 ====================

// ==================== 3D 模型 API ====================

/**
 * 触发3D重建
 * @param {string} patient - 患者ID
 * @param {string} date - 日期
 * @param {string} maskType - 掩码类型 (psoas, muscle, vertebra)
 * @returns {Promise<Object>} - 返回任务信息
 */
export async function reconstruct3D(patient, date, maskType = 'psoas') {
  try {
    const formData = new FormData();
    formData.append('mask_type', maskType);

    const response = await axios.post(
      `${BASE_URL}/reconstruct_3d/${encodeURIComponent(patient)}/${date}`,
      formData
    );
    return response.data;
  } catch (error) {
    console.error('3D重建请求失败:', error);
    throw error;
  }
}

/**
 * 获取3D模型文件
 * @param {string} patient - 患者ID
 * @param {string} date - 日期
 * @param {string} filename - 模型文件名 (如: psoas_3d.stl, muscle_3d.stl)
 * @returns {string} - 3D模型文件的URL
 */
export function get3DModelUrl(patient, date, filename) {
  const token = localStorage.getItem('access_token');
  const params = new URLSearchParams();
  params.append('t', Date.now()); // 缓存破坏
  if (token) {
    params.append('token', token);
  }
  return `${BASE_URL}/get_3d_model/${encodeURIComponent(patient)}/${date}/${filename}?${params.toString()}`;
}

/**
 * 检查3D模型是否存在
 * @param {string} patient - 患者ID
 * @param {string} date - 日期
 * @returns {Promise<Object>} - 返回可用的3D模型列表
 */
export async function check3DModels(patient, date, ownerId = null) {
  try {
    const response = await axios.get(`${BASE_URL}/check_3d_models/${encodeURIComponent(patient)}/${date}${ownerQuery(ownerId)}`);
    return response.data;
  } catch (error) {
    console.error('检查3D模型失败:', error);
    return { available: false, models: [] };
  }
}

/**
 * 获取mask图像列表(用于浏览器端3D重建)
 * @param {string} patient - 患者名称
 * @param {string} date - 研究日期
 * @param {string} maskType - mask类型 ('psoas' 或 'muscle')
 * @returns {Promise<Object>} - 返回mask图像列表
 */
export async function getMaskImages(patient, date, maskType = 'psoas') {
  try {
    const response = await axios.get(
      `${BASE_URL}/get_mask_images/${encodeURIComponent(patient)}/${date}/${maskType}`
    );
    return response.data;
  } catch (error) {
    console.error('获取mask图像列表失败:', error);
    throw error;
  }
}

// ==================== 3D 模型 API 结束 ====================

export { BASE_URL }
